import axios, { AxiosResponse } from 'axios';
import { PageData, NavigationData, SiteConfig, AssetManifest } from '../types/api';

// API Configuration
const API_CONFIG = {
  // Use local dev server in development, CloudFront in production
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 
    (process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3001/api/v1'
      : '/api/v1'),
  
  // Asset URLs - use local dev server in development
  ASSETS_BASE_URL: process.env.REACT_APP_ASSETS_BASE_URL ||
    (process.env.NODE_ENV === 'development'
      ? 'http://localhost:3001'
      : ''),
      
  TIMEOUT: 10000,
};

// Updated to include .json extensions for S3 static file serving
export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    console.error('API Error:', error);
    
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      console.error('Response error:', error.response.status, error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      console.error('Request error:', error.request);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const api = {
  // Pages
  getPage: (slug: string): Promise<AxiosResponse<PageData>> => 
    apiClient.get(`/pages/${slug}.json`),
  
  getContentPage: (slug: string): Promise<AxiosResponse<PageData>> => 
    apiClient.get(`/pages/content/${slug}.json`),
  
  // Navigation
  getNavigation: (): Promise<AxiosResponse<NavigationData>> => 
    apiClient.get('/navigation/primary.json'),
  
  // Configuration
  getSiteConfig: (): Promise<AxiosResponse<SiteConfig>> => 
    apiClient.get('/config/site.json'),
  
  // Assets
  getAssetManifest: (): Promise<AxiosResponse<AssetManifest>> => 
    apiClient.get('/assets/manifest.json'),
};

// Helper functions for common operations
export const apiHelpers = {
  // Get page by slug, handling both section and content pages
  getPageBySlug: async (slug: string): Promise<PageData> => {
    try {
      // Try section page first
      const response = await api.getPage(slug);
      return response.data;
    } catch (error) {
      // If not found, try content page
      try {
        const response = await api.getContentPage(slug);
        return response.data;
      } catch (contentError) {
        throw new Error(`Page not found: ${slug}`);
      }
    }
  },
  
  // Check if a URL is external
  isExternalUrl: (url: string | null): boolean => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  },
  
  // Build asset URL
  buildAssetUrl: (path: string): string => {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      // Already a full URL
      return path;
    }
    
    if (process.env.NODE_ENV === 'development') {
      // Use local dev server for assets
      return `${API_CONFIG.ASSETS_BASE_URL}${path.startsWith('/') ? path : '/' + path}`;
    }
    
    // Production - use relative path
    return path.startsWith('/') ? path : `/${path}`;
  },
};

export default api;
